/**
 * Auto-apply a REMOVABLE discount code (Nunona)
 * ---------------------------------------------
 * Goal: SAVE30 should auto-apply when specific products are in the cart, but
 * still be removable by the customer (so they can clear it and enter a
 * referral/partner code instead).
 *
 * Why this works: automatic discounts CANNOT be removed by the customer.
 * Discount *codes* can. So SAVE30 must be configured in Admin as a DISCOUNT
 * CODE (not "Automatic"), and this script applies that code programmatically.
 * It reuses the theme's own mechanism — POST /cart/update.js with a `discount`
 * field — the exact same call the built-in coupon UI uses, so the code shows in
 * the cart drawer with the theme's existing remove button and carries through to
 * checkout as a removable code. Partner-code landing links (/discount/CODE)
 * keep working unchanged.
 *
 * Behaviour:
 *   - Applies CODE only when a target product is in the cart AND no other code
 *     is already applied (so we never stomp a partner/referral code).
 *   - Applies at most once per cart. If the customer removes it, we do NOT
 *     re-apply it (guarded per cart token in localStorage).
 */
(function () {
  'use strict';

  var CODE = 'SAVE30%';
  var TARGET_PRODUCT_IDS = [9409130496245, 8803429253365];

  var GUARD_PREFIX = 'nunona_autodiscount_';
  var inFlight = false;

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function cartHasTarget(cart) {
    return (cart.items || []).some(function (item) {
      return TARGET_PRODUCT_IDS.indexOf(item.product_id) !== -1;
    });
  }

  // The theme stores the currently applied codes in this cookie (see
  // coupon-code.js). Non-empty => a code is already on the cart; leave it alone.
  function existingCodes() {
    var match = document.cookie.match('(^|;)\\s*eurus_discount_code\\s*=\\s*([^;]+)');
    if (!match) return '';
    var raw = match.pop();
    // Codes may contain a literal '%' (e.g. "SAVE30%"), which makes
    // decodeURIComponent throw — fall back to the raw value in that case.
    try {
      return decodeURIComponent(raw).trim();
    } catch (e) {
      return raw.trim();
    }
  }

  function guardKey(token) {
    return GUARD_PREFIX + token;
  }

  function alreadyHandled(token) {
    try {
      return window.localStorage.getItem(guardKey(token)) === '1';
    } catch (e) {
      return false;
    }
  }

  function markHandled(token) {
    try {
      window.localStorage.setItem(guardKey(token), '1');
    } catch (e) {
      /* private mode — worst case we retry next load, still idempotent */
    }
  }

  function sectionsToRender() {
    try {
      if (window.Alpine && Alpine.store('xCartHelper')) {
        return Alpine.store('xCartHelper').getSectionsToRender();
      }
    } catch (e) {}
    return [];
  }

  function sectionInnerHTML(html, selector) {
    if (typeof window.getSectionInnerHTML === 'function') {
      return window.getSectionInnerHTML(html, selector);
    }
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var el = wrapper.querySelector(selector);
    return el ? el.innerHTML : html;
  }

  function refreshSections(response, sections) {
    sections.forEach(function (section) {
      var el = document.querySelector(section.selector);
      if (el && response.sections && response.sections[section.id]) {
        el.innerHTML = sectionInnerHTML(response.sections[section.id], section.selector);
      }
    });
  }

  function applyCode() {
    console.log('apply code')
    var sections = sectionsToRender();
    var body = { discount: CODE };
    if (sections.length) {
      body.sections = sections.map(function (s) {
        return s.id;
      });
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        if (response.status === 422 || response.status === '422') return false;
        if (sections.length) refreshSections(response, sections);
        // Keep the theme's cookie in sync so its coupon UI reflects the code.
        var applied = (response.discount_codes || [])
          .filter(function (c) {
            return c.applicable;
          })
          .map(function (c) {
            return c.code;
          });
        document.cookie = 'eurus_discount_code=' + applied.join(',') + '; path=/';
        document.dispatchEvent(new CustomEvent('eurus:cart:discount-code:change'));
        return true;
      });
  }

  function maybeApply() {
    if (inFlight) return;
    inFlight = true;

    getCart()
      .then(function (cart) {
        var token = cart.token || 'session';
        if (!cartHasTarget(cart)) return;
        if (alreadyHandled(token)) return; // applied once already, or user removed it
        if (existingCodes()) {
          // A code is already present (e.g. partner/referral) — respect it.
          markHandled(token);
          return;
        }
        return applyCode().then(function (ok) {
          if (ok) markHandled(token);
        });
      })
      .catch(function () {
        /* never break the storefront over a discount */
      })
      .finally(function () {
        inFlight = false;
      });
  }

  document.addEventListener('eurus:cart:items-changed', maybeApply);
  // Skio's subscription plan-picker (skio-plan-picker-component.js) adds to
  // cart itself and fires this event instead of eurus:cart:items-changed.
  document.addEventListener('skio::added-to-cart', maybeApply);
  // Fired by the Intelligems cart-drawer experiment, which hijacks add-to-cart
  // and prevents the theme's own event from ever firing.
  document.addEventListener('ig:cart:items-changed', maybeApply);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeApply);
  } else {
    maybeApply();
  }
})();